import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Users, X } from 'lucide-react'
import { useAuth } from '../../services/authentication/authContext'
import { canAccessSuperAdminDashboard } from '../../services/accessManagement/accessManagement'
import { useImpersonationStore } from '../../stores/appImpersonationStore'
import { getAdminSetupUsers, AdminSetupUser } from '../../services/admin/adminSetupService'

// Dropdown to pick an admin account to "view as". Only rendered for a real super
// admin who is NOT already impersonating (while impersonating, backendUser is the
// target admin and the user-list endpoint would 403 anyway — the banner's Exit is
// the way back).
export const ImpersonationSelector: React.FC = () => {
  const { t } = useTranslation()
  const { backendUser } = useAuth()
  const target = useImpersonationStore(s => s.target)
  const startImpersonation = useImpersonationStore(s => s.startImpersonation)
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState<AdminSetupUser[]>([])
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && users.length === 0) {
      getAdminSetupUsers()
        .then(setUsers)
        .catch(err => console.error('Failed to load admin users for impersonation', err))
    }
  }, [open, users.length])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  if (!backendUser || !canAccessSuperAdminDashboard(backendUser) || target) return null

  const filtered = users.filter(u =>
    // Never offer the super admin their own account — impersonating yourself is a no-op.
    u.id !== backendUser.id &&
    `${u.email} ${u.name || ''}`.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(o => !o)}
        title={t('impersonation.viewAs')}
        className="flex items-center justify-center h-9 w-9 rounded-xl border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        <Users className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 max-h-96 overflow-auto rounded-xl border border-zinc-200 dark:border-borderGrey-dark bg-white dark:bg-background-dark shadow-lg z-50 p-2">
          <p className="px-2 pt-1 pb-2 text-xs font-semibold uppercase tracking-wider text-muted dark:text-muted-dark">
            {t('impersonation.viewAs')}
          </p>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('impersonation.searchPlaceholder')}
            className="w-full mb-2 px-3 py-2 text-sm rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent dark:bg-transparent text-zinc-900 dark:text-zinc-100"
          />
          {filtered.map(u => (
            <button
              key={u.id}
              onClick={() => startImpersonation({ userId: u.id, email: u.email })}
              className="w-full flex flex-col items-start text-left px-3 py-2 rounded-lg hover:bg-cardLightGreen/80 dark:hover:bg-cardLightGreen-dark/80"
            >
              <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{u.name || u.email}</span>
              {u.name && <span className="text-xs text-muted dark:text-muted-dark">{u.email}</span>}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted dark:text-muted-dark">{t('impersonation.noUsers')}</p>
          )}
        </div>
      )}
    </div>
  )
}

// Always-visible banner shown while impersonating, with a one-click exit. Driven
// by the localStorage-backed store so it survives the backendUser→target swap.
export const ImpersonationBanner: React.FC = () => {
  const { t } = useTranslation()
  const { backendUser, accountSettings } = useAuth()
  const { pathname } = useLocation()
  const target = useImpersonationStore(s => s.target)
  const stopImpersonation = useImpersonationStore(s => s.stopImpersonation)
  const setView = useImpersonationStore(s => s.setView)

  if (!target) return null

  return (
    <div className="w-full bg-amber-500 dark:bg-amber-500 text-black dark:text-black text-sm font-medium px-4 py-2 flex flex-wrap lg:flex-nowrap items-center justify-center gap-3">
      <span>{t('impersonation.viewingAs', { email: target.email })}</span>
      {backendUser?.id === target.userId && backendUser.is_super_admin_identity && (
        <button
          type="button"
          onClick={() => setView(backendUser.impersonation_view === 'customer' ? 'admin' : 'customer')}
          className="inline-flex whitespace-nowrap underline font-semibold hover:opacity-80"
        >
          {t(backendUser.impersonation_view === 'customer' ? 'impersonation.adminView' : 'impersonation.customerView')}
        </button>
      )}
      {backendUser?.managed_customer && backendUser.is_super_admin_identity && accountSettings.includes('account_setup') && !pathname.startsWith('/setup') && (
        <Link className="min-h-[44px] inline-flex items-center underline sm:whitespace-nowrap" to="/setup">{t('managedSignup.openSetup')}</Link>
      )}
      <button
        onClick={stopImpersonation}
        className="inline-flex items-center gap-1 underline font-semibold hover:opacity-80"
      >
        <X className="w-4 h-4" />
        {t('impersonation.exit')}
      </button>
    </div>
  )
}
